import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { FetchStatus } from 'store/types';
import { deleteRequest } from 'utils/AjaxUtil';
import AppConstants from 'utils/AppConstants';
import DataUrlConstants from 'utils/DataUrlConstants';
import { makeUrl } from 'utils/UrlUtil';

export interface SchemaState {
  deletingFieldStatus: FetchStatus;
  deletingFieldError: string | null;
  deletingEntityStatus: FetchStatus;
  deletingEntityError: string | null;
}

export const initialState: SchemaState = {
  deletingFieldStatus: AppConstants.FETCH_STATUS.IDLE,
  deletingFieldError: null,
  deletingEntityStatus: AppConstants.FETCH_STATUS.IDLE,
  deletingEntityError: null,
};

export interface UpdateDeleteSchemaFieldArgs {
  entityId: string;
  fieldId: string;
}

export interface DeleteSchemaEntityArgs {
  entityId: string;
}

export const deleteSchemaField = createAsyncThunk(
  'schema/field/delete',
  ({ entityId, fieldId }: UpdateDeleteSchemaFieldArgs, { rejectWithValue }) =>
    deleteRequest(makeUrl(DataUrlConstants.UPDATE_FIELD, { entityId, fieldId }))
      .then((resp) => resp.data)
      .catch((error) => rejectWithValue(error?.response?.data?.message))
);

export const deleteSchemaEntity = createAsyncThunk(
  'schema/entity/delete',
  ({ entityId }: DeleteSchemaEntityArgs, { rejectWithValue }) =>
    deleteRequest(makeUrl(DataUrlConstants.ENTITY, { syncariEntityId: entityId }))
      .then((resp) => resp.data)
      .catch((error) => rejectWithValue(error?.response?.data?.message))
);

const schemaSlice = createSlice({
  name: 'schema',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(deleteSchemaField.pending, (state, action) => {
      state.deletingFieldStatus = AppConstants.FETCH_STATUS.LOADING;
      state.deletingFieldError = null;
    });

    builder.addCase(deleteSchemaField.fulfilled, (state, action) => {
      state.deletingFieldStatus = AppConstants.FETCH_STATUS.SUCCESS;
    });

    builder.addCase(deleteSchemaField.rejected, (state, action) => {
      state.deletingFieldStatus = AppConstants.FETCH_STATUS.ERROR;
      state.deletingFieldError = typeof action.payload === 'string' ? action.payload : 'An unknown error occurred';
    });

    builder.addCase(deleteSchemaEntity.pending, (state) => {
      state.deletingEntityStatus = AppConstants.FETCH_STATUS.LOADING;
      state.deletingEntityError = null;
    });

    builder.addCase(deleteSchemaEntity.fulfilled, (state) => {
      state.deletingEntityStatus = AppConstants.FETCH_STATUS.SUCCESS;
    });

    builder.addCase(deleteSchemaEntity.rejected, (state, action) => {
      state.deletingEntityStatus = AppConstants.FETCH_STATUS.ERROR;
      state.deletingEntityError = typeof action.payload === 'string' ? action.payload : 'An unknown error occurred';
    });
  },
});

export const { reducer } = schemaSlice;
